import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import logoBlack from '../assets/logo-black.png';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '/', scrollTo: '#home' },
    { name: 'Services', href: '/', scrollTo: '#services' },
    { name: 'Portfolio', href: '/', scrollTo: '#portfolio' },
    { name: 'Book Now', href: '/', scrollTo: '#booking' },
    { name: 'Contact', href: '/', scrollTo: '#contact' },
  ];

  const handleNavigation = (href: string, scrollTo?: string) => {
    if (location.pathname === '/') {
      // On home page, scroll to section
      if (scrollTo) {
        const element = document.querySelector(scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      // On sub-pages, navigate to home page first, then scroll
      navigate(href + (scrollTo || ''));
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-md shadow-lg border-b border-black/5'
          : 'bg-gradient-to-b from-black/40 to-transparent backdrop-blur-sm'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center cursor-pointer group relative"
            onClick={() => handleNavigation('/', '#home')}
            initial="rest"
            animate="rest"
            whileHover="hover"
          >
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="w-12 h-12 flex items-center justify-center relative z-10"
            >
              <img 
                src={isScrolled ? logoBlack : logo} 
                alt="Book My Shoot Logo" 
                className={`w-10 h-10 object-contain ${isScrolled ? '' : 'filter brightness-0 invert'}`}
              />
            </motion.div>
            <motion.span
              variants={{
                rest: { x: -56, opacity: 0 },
                hover: { x: 0, opacity: 1 }
              }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className={`font-bold text-xl ml-3 whitespace-nowrap ${
                isScrolled ? 'text-black' : 'text-white'
              }`}
            >
              BOOK MY SHOOT
            </motion.span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href, item.scrollTo)}
                className={`relative font-medium transition-colors duration-200 ${
                  isScrolled ? 'text-gray-700 hover:text-black' : 'text-white hover:text-gray-200'
                }`}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 hover:w-full"></span>
              </button>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-white/95 backdrop-blur-md rounded-lg mt-2 p-4 shadow-lg"
          >
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href, item.scrollTo)}
                className="block w-full text-left py-2 text-gray-700 hover:text-black font-medium transition-colors duration-200"
              >
                {item.name}
              </button>
            ))}
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
};

export default Header;
